import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportCheckboxField,
  OtherReportFilterCard,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSelectField,
  OtherReportShell,
  OtherReportToolbar,
  OtherReportEmptyCells,
} from './other-reports/OtherReportShared';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const FleetCardStatementReport = (props) => {
  const [meta, setMeta] = useState({ fleet_card_heads: [] });
  const [inputValues, setInputValues] = useState({ summary: false });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/fleetcard_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Fleet Card Statement', error.message, 'error'));
  }, []);

  const loadReport = async () => {
    if (!inputValues.start_date || !inputValues.end_date || !inputValues.trans_head) {
      swal('Please select dates and fleet card account head', '', 'warning');
      return;
    }

    setLoading(true);
    setReport(null);
    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      trans_head: inputValues.trans_head,
    };
    if (inputValues.summary) payload.summary = 1;

    try {
      setReport(await fetchReportJson(`${API_URL}/fleetcard_statement`, payload));
    } catch (error) {
      swal('Fleet Card Statement', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildPayload = () => {
    const payload = {
      from_date: inputValues.start_date,
      to_date: inputValues.end_date,
      trans_head: inputValues.trans_head,
    };
    if (inputValues.summary) {
      payload.summary = 1;
    }
    return payload;
  };

  const downloadPdf = async () => {
    if (!inputValues.start_date || !inputValues.end_date || !inputValues.trans_head) {
      swal('Please select dates and fleet card account head', '', 'warning');
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/fleetcard_statement/pdf`,
        buildPayload(),
        'fleetcard_statement.pdf',
      );
    } catch (error) {
      swal('Fleet Card Statement', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  const openingRows = (report?.opening_balance || []).filter((row) => Number(row.outstanding || 0) !== 0);

  const totalsRow = useMemo(() => {
    if (report?.totals_row) {
      return report.totals_row;
    }
    const rows = report?.rows || [];
    return {
      label: 'Total',
      quantity: rows.reduce((sum, row) => sum + toNumber(row.quantity), 0),
      price: rows.reduce((sum, row) => sum + toNumber(row.price), 0),
      amount: rows.reduce((sum, row) => sum + toNumber(row.amount), 0),
      transfer_amount_ioc: rows.reduce((sum, row) => sum + toNumber(row.transfer_amount_ioc), 0),
    };
  }, [report]);

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Fleet Card Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                    <OtherReportSelectField
                      label="Account head"
                      value={inputValues.trans_head}
                      placeholder="Select fleet card"
                      span={3}
                      onChange={(value) => setInputValues({ ...inputValues, trans_head: value })}
                    >
                      {(meta.fleet_card_heads || []).map((head) => (
                        <option key={head.id} value={head.id}>{head.accounts_head}</option>
                      ))}
                    </OtherReportSelectField>
                    <OtherReportCheckboxField
                      id="fleet-summary-toggle"
                      label="Summary mode"
                      checked={inputValues.summary}
                      onChange={(checked) => setInputValues({ ...inputValues, summary: checked })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating fleet card statement" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name}</h2>
                    <h4 className="table-title mb-1">{report.type}</h4>
                    <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                    <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                  </div>
                </div>
                <table className="table border">
                  <thead>
                    {report.is_summary ? (
                      <tr>
                        <th>Date</th><th>Amount</th><th>Transfer to dealer</th><th>Outstanding</th>
                      </tr>
                    ) : (
                      <tr>
                        <th>Date</th><th>Description</th><th>Particulars</th><th>Product</th>
                        <th>Qty</th><th>Price</th><th>Amount</th><th>Outstanding</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {openingRows.map((row, i) => (
                      <tr key={`open-${i}`} className="table-light">
                        <td>{formatReportDate(report.start_date)}</td>
                        {report.is_summary ? (
                          <>
                            <OtherReportEmptyCells count={2} />
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                          </>
                        ) : (
                          <>
                            <td>{row.description || 'Opening Balance'}</td>
                            <OtherReportEmptyCells count={4} />
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                            <OtherReportEmptyCells count={1} />
                          </>
                        )}
                      </tr>
                    ))}
                    {(report.rows || []).map((row, i) => (
                      <tr key={`row-${i}`}>
                        <td>{formatReportDate(row.ac_date)}</td>
                        {report.is_summary ? (
                          <>
                            <td className="report-amount">{reportNumberFormat(row.amount)}</td>
                            <td className="report-amount">{reportNumberFormat(row.transfer_amount_ioc)}</td>
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                          </>
                        ) : (
                          <>
                            <td>{row.description || ''}</td>
                            <td>{row.particulars || ''}</td>
                            <td>{row.product || ''}</td>
                            <td className="report-amount">{row.quantity ?? ''}</td>
                            <td className="report-amount">{row.price ? reportNumberFormat(row.price) : ''}</td>
                            <td className="report-amount">{reportNumberFormat(row.amount)}</td>
                            <td className="report-amount">{reportNumberFormat(row.outstanding)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                    {(report.rows || []).length > 0 && (
                      <tr className="fw-bold">
                        {report.is_summary ? (
                          <>
                            <td>{totalsRow.label || 'Total'}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.amount)}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.transfer_amount_ioc)}</td>
                            <td />
                          </>
                        ) : (
                          <>
                            <td colSpan={4}>{totalsRow.label || 'Total'}</td>
                            <td className="report-amount">{totalsRow.quantity}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.price)}</td>
                            <td className="report-amount">{reportNumberFormat(totalsRow.amount)}</td>
                            <td />
                          </>
                        )}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default FleetCardStatementReport;
